/**
 * Microsoft sign-in, using the **redirect** flow.
 *
 * The client id is public by design — every SPA ships it in its bundle. There
 * is no client secret anywhere in this project; MSAL uses the authorization
 * code flow with PKCE, which is why the Azure app registration must use the
 * "Single-page application" platform and not "Web".
 *
 * Redirect rather than popup means the whole page navigates to Microsoft and
 * back, so two things matter:
 *
 * 1. `handleRedirectPromise()` must run on **every** page load, before any
 *    other MSAL call, and only the first caller receives the result. That is
 *    why it lives in one shared promise here instead of being called ad hoc.
 * 2. Any state the user had in memory is gone by the time they return, and
 *    MSAL v5 always lands back on `redirectUri` rather than the page that
 *    started sign-in. The caller is responsible for parking whatever it needs
 *    to resume — see `state/sync.ts`, which leaves a note in sessionStorage
 *    before navigating, and `ui/App.tsx`, which restores the active tab.
 */

import {
  PublicClientApplication,
  type AccountInfo,
  type AuthenticationResult,
  type Configuration,
} from '@azure/msal-browser';
import { classifyTokenFailure } from './authFailure.ts';

const CLIENT_ID = import.meta.env.VITE_MSAL_CLIENT_ID || 'a2dd0b1f-d384-40ea-bd76-38085cb9ba07';
const AUTHORITY =
  import.meta.env.VITE_MSAL_AUTHORITY || 'https://login.microsoftonline.com/consumers';
const REDIRECT_URI =
  typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173';

/**
 * `Files.ReadWrite.AppFolder` and nothing broader. The app can only ever see
 * the folder OneDrive creates for it — the rest of the drive is invisible to
 * this code, which is a property of the token, not a promise in a comment.
 */
export const SCOPES = ['Files.ReadWrite.AppFolder', 'User.Read', 'offline_access'];

export function isSyncConfigured(): boolean {
  return CLIENT_ID.length > 0;
}

const configuration: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: AUTHORITY,
    redirectUri: REDIRECT_URI,
    // Without this, signing out strands the user on a Microsoft "you have
    // signed out" page instead of returning to the app. It must be one of the
    // URIs registered on the app registration.
    postLogoutRedirectUri: REDIRECT_URI,
  },
  cache: {
    // localStorage rather than sessionStorage: the redirect flow reloads the
    // page, and on a personal device being asked to sign in again every time
    // would defeat the point.
    cacheLocation: 'localStorage',
  },
};

export interface AuthInit {
  app: PublicClientApplication;
  /** Non-null only on the load that is returning from a sign-in redirect. */
  redirectResult: AuthenticationResult | null;
}

let ready: Promise<AuthInit> | null = null;

/**
 * Initialize MSAL and consume any pending redirect response. Idempotent — every
 * caller awaits the same promise, so `handleRedirectPromise` runs exactly once
 * per page load, as MSAL requires.
 */
export function initializeAuth(): Promise<AuthInit> {
  if (!isSyncConfigured()) {
    return Promise.reject(new Error('VITE_MSAL_CLIENT_ID is not set'));
  }

  if (ready === null) {
    ready = (async () => {
      const app = new PublicClientApplication(configuration);
      await app.initialize();

      // Must come before any other MSAL API call on this page load.
      const redirectResult = await app.handleRedirectPromise();

      const account = redirectResult?.account ?? app.getAllAccounts()[0] ?? null;
      if (account !== null) app.setActiveAccount(account);

      return { app, redirectResult };
    })();
  }
  return ready;
}

export async function getAccount(): Promise<AccountInfo | null> {
  if (!isSyncConfigured()) return null;
  const { app } = await initializeAuth();
  return app.getActiveAccount() ?? app.getAllAccounts()[0] ?? null;
}

/**
 * Starts sign-in by navigating away. This does not resolve in any useful sense:
 * the page is replaced. Anything that should happen "after sign-in" has to be
 * arranged before calling, and picked up on the next load.
 */
export async function signIn(): Promise<never> {
  const { app } = await initializeAuth();
  await app.loginRedirect({ scopes: SCOPES, prompt: 'select_account' });
  // Unreachable in practice; keeps the type honest about never returning.
  return new Promise<never>(() => {});
}

export async function signOut(): Promise<void> {
  const { app } = await initializeAuth();
  const account = app.getActiveAccount() ?? app.getAllAccounts()[0];
  if (account === undefined || account === null) return;
  await app.logoutRedirect({ account });
}

/** Thrown when a token needs interaction and a redirect has been started. */
export class InteractionStartedError extends Error {
  constructor() {
    super('Redirecting for sign-in');
    this.name = 'InteractionStartedError';
  }
}

/**
 * Thrown when the cached session can no longer produce a token and only signing
 * in again will fix it. Distinct from a generic error so the UI can offer the
 * one action that works, instead of showing a failure the user has to diagnose.
 */
export class AuthExpiredError extends Error {
  constructor(detail: string) {
    super(detail);
    this.name = 'AuthExpiredError';
  }
}

/**
 * How long a silent acquisition may take before it is treated as a spent
 * session.
 *
 * MSAL retries internally against the token endpoint, so a stale refresh token
 * does not fail fast — it stalls. Without a ceiling the sync appears to run for
 * minutes and then reports nothing useful, which is exactly the failure this
 * bound exists to convert into an actionable one.
 */
const SILENT_TOKEN_TIMEOUT_MS = 15_000;

function withTimeout<T>(work: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new AuthExpiredError(label)), ms);
    work.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error: unknown) => {
        clearTimeout(timer);
        reject(error as Error);
      },
    );
  });
}

/**
 * Access token for Graph. Silent first — normal use never interrupts, because
 * the refresh token is cached.
 *
 * Every way this can fail now has an answer. Interaction required starts the
 * redirect, as before. A session that is simply spent raises `AuthExpiredError`
 * rather than being rethrown as an anonymous failure — previously that landed
 * in the sync's generic error branch, which said nothing about signing in and
 * left "log out and back in again" as something to work out by hand. A network
 * failure is rethrown untouched, because redirecting to Microsoft over a
 * dropped connection would be worse than waiting.
 */
export async function getAccessToken(): Promise<string> {
  const { app } = await initializeAuth();
  const account = app.getActiveAccount() ?? app.getAllAccounts()[0];
  if (account === undefined || account === null) throw new Error('not-signed-in');

  try {
    const result = await withTimeout(
      app.acquireTokenSilent({ scopes: SCOPES, account }),
      SILENT_TOKEN_TIMEOUT_MS,
      `Silent sign-in did not complete within ${SILENT_TOKEN_TIMEOUT_MS / 1000}s`,
    );
    return result.accessToken;
  } catch (error) {
    if (error instanceof AuthExpiredError) throw error;

    switch (classifyTokenFailure(error, { online: navigator.onLine })) {
      case 'interaction-required':
        await app.acquireTokenRedirect({ scopes: SCOPES, account });
        throw new InteractionStartedError();
      case 'expired':
        throw new AuthExpiredError(error instanceof Error ? error.message : String(error));
      case 'transient':
        throw error;
    }
  }
}

/**
 * Sign in again over a session that has expired, without the sign-out first.
 *
 * `prompt: 'login'` is the point: it forces a fresh interactive authentication
 * and replaces the cached tokens, which is what the manual sign-out/sign-in
 * dance was really achieving — in one redirect instead of two.
 */
export async function reauthenticate(): Promise<never> {
  const { app } = await initializeAuth();
  const account = app.getActiveAccount() ?? app.getAllAccounts()[0] ?? null;
  await app.acquireTokenRedirect({
    scopes: SCOPES,
    prompt: 'login',
    ...(account === null ? {} : { account }),
  });
  return new Promise<never>(() => {});
}
